"use client";

import React, { useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

export type Gait = { phase: number; amp: number };

const box = new THREE.BoxGeometry(1, 1, 1);
const sph = new THREE.SphereGeometry(1, 10, 8);
const sphSm = new THREE.SphereGeometry(1, 7, 6);
const cyl = new THREE.CylinderGeometry(1, 1, 1, 7);
const cone = new THREE.ConeGeometry(1, 1, 6);
const cap = new THREE.CapsuleGeometry(1, 1, 3, 6);

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _e = new THREE.Euler();

function part(
  src: THREE.BufferGeometry,
  color: string,
  x: number,
  y: number,
  z: number,
  rx = 0,
  ry = 0,
  rz = 0,
  sx = 1,
  sy = 1,
  sz = 1,
  limb = 0,
  pivot = 0
) {
  const g = src.clone();
  const n = g.attributes.position.count;
  const col = new THREE.Color(color);
  const arr = new Float32Array(n * 3);
  const limbs = new Float32Array(n);
  const pivots = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = col.r;
    arr[i * 3 + 1] = col.g;
    arr[i * 3 + 2] = col.b;
    limbs[i] = limb;
    pivots[i] = pivot;
  }
  g.setAttribute("color", new THREE.BufferAttribute(arr, 3));
  g.setAttribute("limb", new THREE.BufferAttribute(limbs, 1));
  g.setAttribute("pivot", new THREE.BufferAttribute(pivots, 1));
  _e.set(rx, ry, rz);
  _q.setFromEuler(_e);
  _p.set(x, y, z);
  _s.set(sx, sy, sz);
  _m.compose(_p, _q, _s);
  g.applyMatrix4(_m);
  return g;
}

/** Neck cylinder whose far end sits inside the head sphere. */
function neckJoin(baseY: number, baseZ: number, neckLen: number, headR: number, ang: number) {
  const ay = Math.cos(ang);
  const az = Math.sin(ang);
  const cy = baseY + ay * neckLen * 0.5;
  const cz = baseZ + az * neckLen * 0.5;
  return {
    ang,
    cy,
    cz,
    hy: cy + ay * (neckLen * 0.5 + headR * 0.35),
    hz: cz + az * (neckLen * 0.5 + headR * 0.35),
  };
}

function fuse(parts: THREE.BufferGeometry[]) {
  const g = mergeGeometries(parts, false);
  if (!g) throw new Error("figure merge failed");
  g.computeVertexNormals();
  return g;
}

function leg(
  out: THREE.BufferGeometry[],
  x: number,
  z: number,
  coat: string,
  hoof: string,
  step: number,
  thick: number,
  tall: number
) {
  const z1 = z + step;
  const limb = x < 0 ? (z >= 0 ? 1 : 3) : z >= 0 ? 2 : 4;
  const pivot = tall * 0.92;
  out.push(part(cap, coat, x, tall * 0.62, z1, 0, 0, 0, thick, tall * 0.42, thick * 0.85, limb, pivot));
  out.push(part(cyl, coat, x, tall * 0.28, z1 * 0.65, 0, 0, 0, thick * 0.62, tall * 0.36, thick * 0.62, limb, pivot));
  out.push(part(box, hoof, x, 0.045, z + step * 0.45, 0, 0, 0, thick * 1.15, 0.07, thick * 1.7, limb, pivot));
}

function WalkMat({
  gait,
  roughness,
  metalness,
}: {
  gait?: React.MutableRefObject<Gait>;
  roughness: number;
  metalness: number;
}) {
  const mat = useMemo(() => {
    const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness, metalness });
    m.onBeforeCompile = (shader) => {
      shader.uniforms.uPhase = { value: 0 };
      shader.uniforms.uAmp = { value: 0 };
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          `#include <common>
attribute float limb;
attribute float pivot;
uniform float uPhase;
uniform float uAmp;
float walkAngle() {
  float sign = 1.0;
  if (limb > 1.5 && limb < 2.5) sign = -1.0;
  else if (limb > 2.5 && limb < 3.5) sign = -1.0;
  else if (limb > 3.5 && limb < 4.5) sign = 1.0;
  else if (limb > 4.5 && limb < 5.5) sign = -1.0;
  else if (limb > 5.5) sign = 1.0;
  float reach = limb > 4.5 ? 0.65 : 0.55;
  return sin(uPhase) * sign * reach * uAmp;
}`
        )
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
transformed.y += uAmp * abs(sin(uPhase * 2.0)) * 0.03;
if (limb > 0.5) {
  float a = walkAngle();
  float y = transformed.y - pivot;
  float z = transformed.z;
  transformed.y = pivot + y * cos(a) - z * sin(a);
  transformed.z = z * cos(a) + y * sin(a);
}`
        )
        .replace(
          "#include <beginnormal_vertex>",
          `#include <beginnormal_vertex>
if (limb > 0.5) {
  float a = walkAngle();
  float ny = objectNormal.y;
  float nz = objectNormal.z;
  objectNormal.y = ny * cos(a) - nz * sin(a);
  objectNormal.z = nz * cos(a) + ny * sin(a);
}`
        );
      m.userData.shader = shader;
    };
    return m;
  }, [roughness, metalness]);
  useFrame(() => {
    const shader = mUser(mat);
    if (!shader) return;
    shader.uniforms.uPhase.value = gait?.current.phase || 0;
    shader.uniforms.uAmp.value = gait?.current.amp || 0;
  });
  useEffect(() => () => mat.dispose(), [mat]);
  return <primitive object={mat} attach="material" />;
}

function mUser(mat: THREE.Material): { uniforms: { uPhase: { value: number }; uAmp: { value: number } } } | null {
  const shader = (mat.userData as { shader?: { uniforms: { uPhase: { value: number }; uAmp: { value: number } } } }).shader;
  return shader || null;
}

function horseHide(knight: boolean) {
  const coat = knight ? "#5c3318" : "#8d4b28";
  const dark = knight ? "#2a180e" : "#3a2416";
  const mane = knight ? "#1a120c" : "#2c1810";
  const hoof = "#1c140e";
  const out: THREE.BufferGeometry[] = [];
  out.push(part(cap, coat, 0, 0.78, 0.02, Math.PI / 2, 0, 0, 0.3, 0.78, 0.34));
  out.push(part(sph, coat, 0, 0.8, 0.42, 0, 0, 0, 0.3, 0.28, 0.32));
  out.push(part(sph, coat, 0, 0.84, -0.4, 0, 0, 0, 0.28, 0.26, 0.3));
  const horseNeck = neckJoin(0.92, 0.48, 0.46, 0.13, 0.9);
  out.push(part(cap, coat, 0, horseNeck.cy, horseNeck.cz, horseNeck.ang, 0, 0, 0.12, 0.46, 0.13));
  out.push(part(sph, coat, 0, horseNeck.hy, horseNeck.hz, 0, 0, 0, 0.13, 0.12, 0.16));
  out.push(part(box, coat, 0, horseNeck.hy - 0.06, horseNeck.hz + 0.16, 0.15, 0, 0, 0.12, 0.1, 0.22));
  out.push(part(box, dark, 0, horseNeck.hy - 0.1, horseNeck.hz + 0.26, 0, 0, 0, 0.1, 0.06, 0.08));
  out.push(part(cone, coat, -0.07, horseNeck.hy + 0.1, horseNeck.hz - 0.02, 0, 0, 0.2, 0.035, 0.1, 0.045));
  out.push(part(cone, coat, 0.07, horseNeck.hy + 0.1, horseNeck.hz - 0.02, 0, 0, -0.2, 0.035, 0.1, 0.045));
  out.push(part(sphSm, "#1a1a1a", -0.07, horseNeck.hy + 0.02, horseNeck.hz + 0.08, 0, 0, 0, 0.028, 0.028, 0.028));
  out.push(part(sphSm, "#1a1a1a", 0.07, horseNeck.hy + 0.02, horseNeck.hz + 0.08, 0, 0, 0, 0.028, 0.028, 0.028));
  for (let i = 0; i < 6; i++) {
    const t = i / 5;
    out.push(
      part(cone, mane, 0, 1.22 + (1 - t) * 0.18, 0.48 + t * 0.38, -0.6, 0, 0, 0.04, 0.12, 0.05)
    );
  }
  out.push(part(cone, mane, 0, 0.95, -0.72, 0.9, 0, 0, 0.06, 0.28, 0.06));
  out.push(part(cone, mane, 0, 0.72, -0.86, 1.2, 0, 0, 0.05, 0.22, 0.05));
  leg(out, -0.16, 0.32, dark, hoof, 0.08, 0.11, 0.78);
  leg(out, 0.16, 0.28, dark, hoof, -0.06, 0.11, 0.78);
  leg(out, -0.16, -0.32, dark, hoof, -0.05, 0.12, 0.8);
  leg(out, 0.16, -0.28, dark, hoof, 0.1, 0.12, 0.8);
  out.push(part(box, "#6a3a22", 0, 0.96, -0.02, 0, 0, 0, 0.28, 0.08, 0.36));
  out.push(part(box, "#4a2818", 0, 0.9, 0.22, 0.4, 0, 0, 0.16, 0.06, 0.22));
  return fuse(out);
}

function riderMetal(accent: string, knight: boolean) {
  const steel = knight ? "#8e96a0" : "#6a6258";
  const cloth = accent;
  const out: THREE.BufferGeometry[] = [];
  out.push(part(box, knight ? steel : "#6a4a2c", 0, 1.18, 0.02, 0, 0, 0, 0.34, 0.3, 0.24));
  out.push(part(box, cloth, 0, 1.22, 0.12, 0, 0, 0, 0.16, 0.18, 0.04));
  out.push(part(sph, "#d7b394", 0, 1.48, 0.04, 0, 0, 0, 0.11, 0.12, 0.11));
  out.push(part(cyl, knight ? steel : "#6a4a2c", 0, 1.58, 0.02, 0, 0, 0, knight ? 0.14 : 0.12, 0.14, knight ? 0.14 : 0.12));
  out.push(part(box, cloth, 0, 1.72, 0.02, 0, 0, 0, 0.06, 0.14, 0.04));
  if (knight) {
    out.push(part(box, steel, 0, 1.5, 0.14, 0, 0, 0, 0.16, 0.08, 0.06));
    out.push(part(box, steel, 0, 0.9, 0.22, 0.4, 0, 0, 0.28, 0.16, 0.08));
    out.push(part(box, cloth, -0.22, 1.12, 0.16, 0, 0.2, 0, 0.08, 0.22, 0.04));
  } else {
    out.push(part(box, "#c4a06a", 0, 1.66, 0.02, 0, 0, 0, 0.16, 0.06, 0.14));
    out.push(part(box, cloth, -0.18, 1.1, 0.14, 0, 0.3, 0, 0.06, 0.16, 0.04));
  }
  out.push(part(cap, cloth, -0.2, 0.95, 0.18, 0.5, 0, 0.4, 0.06, 0.28, 0.07, 5, 1.15));
  out.push(part(cap, cloth, 0.2, 0.95, 0.12, 0.4, 0, -0.4, 0.06, 0.28, 0.07, 6, 1.15));
  out.push(part(cyl, "#3a2a18", 0.34, 1.35, 0.16, 0.3, 0, -0.15, 0.025, 0.85, 0.025));
  out.push(part(cone, steel, 0.4, 1.74, 0.28, 0.3, 0, -0.15, 0.045, 0.16, 0.045));
  out.push(part(cyl, "#2a2018", -0.16, 1.22, 0.55, Math.PI / 2, 0, 0, 0.012, 0.4, 0.012));
  out.push(part(cyl, "#2a2018", 0.16, 1.22, 0.55, Math.PI / 2, 0, 0, 0.012, 0.4, 0.012));
  return fuse(out);
}

const horseCache = new Map<string, { hide: THREE.BufferGeometry; metal: THREE.BufferGeometry }>();

export function HorseRider({
  accent,
  knight,
  bob,
  gait,
}: {
  accent: string;
  knight?: boolean;
  bob: number;
  gait?: React.MutableRefObject<Gait>;
}) {
  const geos = useMemo(() => {
    const key = `${accent}|${knight ? 1 : 0}`;
    let hit = horseCache.get(key);
    if (!hit) {
      hit = { hide: horseHide(!!knight), metal: riderMetal(accent, !!knight) };
      horseCache.set(key, hit);
    }
    return hit;
  }, [accent, knight]);
  return (
    <group position={[0, bob * 0.4, 0]} scale={1.2}>
      <mesh geometry={geos.hide} castShadow receiveShadow>
        <WalkMat gait={gait} roughness={0.68} metalness={0.02} />
      </mesh>
      <mesh geometry={geos.metal} castShadow>
        <WalkMat gait={gait} roughness={0.42} metalness={0.45} />
      </mesh>
    </group>
  );
}

function beastGeo(kind: string) {
  const out: THREE.BufferGeometry[] = [];
  if (kind === "chicken") {
    const f = "#f3efe4";
    out.push(part(sph, f, 0, 0.2, 0, 0, 0, 0, 0.14, 0.13, 0.18));
    out.push(part(sph, f, 0, 0.32, 0.14, 0, 0, 0, 0.08, 0.08, 0.09));
    out.push(part(cone, "#e07020", 0, 0.3, 0.24, Math.PI / 2, 0, 0, 0.03, 0.08, 0.03));
    out.push(part(sphSm, "#d02020", 0, 0.4, 0.12, 0, 0, 0, 0.03, 0.045, 0.02));
    out.push(part(box, "#e8a030", -0.1, 0.24, -0.02, 0.2, 0.3, 0.6, 0.14, 0.03, 0.09));
    out.push(part(box, "#e8a030", 0.1, 0.24, -0.02, 0.2, -0.3, -0.6, 0.14, 0.03, 0.09));
    out.push(part(cone, "#d08040", -0.05, 0.08, 0.04, 0, 0, 0, 0.015, 0.1, 0.015, 1, 0.16));
    out.push(part(cone, "#d08040", 0.05, 0.08, 0.04, 0, 0, 0, 0.015, 0.1, 0.015, 2, 0.16));
    out.push(part(box, "#c07030", -0.05, 0.02, 0.08, 0, 0, 0, 0.05, 0.015, 0.02));
    out.push(part(box, "#c07030", 0.05, 0.02, 0.08, 0, 0, 0, 0.05, 0.015, 0.02));
    return fuse(out);
  }
  if (kind === "duck") {
    out.push(part(cap, "#2f6a32", 0, 0.16, 0, Math.PI / 2, 0, 0, 0.1, 0.22, 0.11));
    out.push(part(sph, "#3d7a38", 0, 0.24, 0.16, 0, 0, 0, 0.08, 0.08, 0.08));
    out.push(part(box, "#e8a030", 0, 0.22, 0.26, 0, 0, 0, 0.07, 0.025, 0.1));
    out.push(part(box, "#4a8a40", -0.08, 0.18, -0.02, 0.2, 0, 0.4, 0.12, 0.02, 0.08));
    out.push(part(box, "#e09020", -0.04, 0.04, 0.06, 0, 0, 0, 0.04, 0.02, 0.06, 1, 0.08));
    out.push(part(box, "#e09020", 0.04, 0.04, 0.06, 0, 0, 0, 0.04, 0.02, 0.06, 2, 0.08));
    return fuse(out);
  }
  return fuse(hoofed(kind));
}

/** Length along Z, width along X, height along Y. Capsule native size is 2 × 3 × 2. */
function torso(color: string, y: number, length: number, width: number, height: number) {
  return part(cap, color, 0, y, 0, Math.PI / 2, 0, 0, width / 2, length / 3, height / 2);
}

function hoofed(kind: string) {
  const sheep = kind === "sheep";
  const cow = kind === "cow";
  const goat = kind === "goat";
  const deer = kind === "deer";
  const lion = kind === "lion";
  const coyote = kind === "coyote";
  const coat = sheep
    ? "#f7f4ee"
    : cow
      ? "#7a4a2c"
      : goat
        ? "#cbb89a"
        : deer
          ? "#8d5a32"
          : lion
            ? "#e0b15a"
            : coyote
              ? "#c4a06a"
              : "#2c2c2c";
  const dark = sheep ? "#2a2a2a" : deer ? "#6b4124" : lion ? "#c4923e" : coyote ? "#8a6840" : "#1a1a1a";
  const hoof = sheep ? "#1a1a1a" : "#24160e";
  const len = sheep ? 0.5 : cow ? 0.78 : deer ? 0.7 : lion ? 0.85 : 0.62;
  const wide = sheep ? 0.34 : cow ? 0.34 : deer ? 0.18 : lion ? 0.32 : 0.22;
  const deep = sheep ? 0.26 : cow ? 0.28 : deer ? 0.18 : 0.24;
  const shoulder = sheep ? 0.55 : cow ? 0.78 : deer ? 0.86 : lion ? 0.66 : 0.55;
  const out: THREE.BufferGeometry[] = [];
  if (sheep) {
    const lumps: Array<[number, number, number, number]> = [
      [0, shoulder, -0.02, 1],
      [0, shoulder + 0.05, -0.08, 0.82],
      [-0.08, shoulder + 0.02, 0.02, 0.7],
      [0.08, shoulder + 0.02, -0.06, 0.68],
    ];
    for (const [x, y, z, s] of lumps) {
      out.push(part(sph, coat, x, y, z, 0, 0, 0, wide * 0.55 * s, deep * 0.65 * s, len * 0.42 * s));
    }
  } else {
    out.push(part(box, coat, 0, shoulder, -0.02, 0, 0, 0, wide, deep, len * 0.85));
    out.push(part(box, coat, 0, shoulder + deep * 0.08, len * 0.38, 0, 0, 0, wide * 0.72, deep * 0.72, len * 0.32));
    out.push(part(sph, coat, 0, shoulder, -len * 0.4, 0, 0, 0, wide * 0.42, deep * 0.42, len * 0.18));
    if (cow) {
      out.push(part(box, "#f4efe6", wide * 0.38, shoulder + 0.02, 0.02, 0, 0.1, 0, 0.025, deep * 0.4, len * 0.2));
      out.push(part(box, "#f4efe6", -wide * 0.36, shoulder, -0.14, 0, -0.15, 0, 0.025, deep * 0.32, len * 0.14));
      out.push(part(sph, "#e7b4bc", 0, shoulder - deep * 0.45, 0, 0, 0, 0, 0.08, 0.06, 0.08));
    }
  }
  const neckLen = deer ? 0.46 : cow ? 0.32 : 0.22;
  const hr = deer ? 0.1 : cow ? 0.14 : sheep ? 0.11 : 0.12;
  const joined = neckJoin(shoulder + 0.02, len * (sheep ? 0.2 : 0.4), neckLen, hr, deer ? 1.05 : 0.82);
  const { hy, hz } = joined;
  out.push(part(cyl, dark, 0, joined.cy, joined.cz, joined.ang, 0, 0, Math.max(0.04, wide * 0.18), neckLen, Math.max(0.035, wide * 0.16)));
  out.push(part(sph, dark, 0, hy, hz, 0, 0, 0, hr, hr * 0.9, hr * 1.05));
  out.push(part(box, dark, 0, hy - hr * 0.2, hz + hr * 0.95, 0, 0, 0, hr * 0.7, hr * 0.45, hr * 0.9));
  out.push(part(box, "#1a100c", 0, hy - hr * 0.05, hz + hr * 1.45, 0, 0, 0, hr * 0.4, hr * 0.22, 0.03));
  out.push(part(sphSm, "#111", -hr * 0.55, hy + hr * 0.2, hz + hr * 0.45, 0, 0, 0, 0.02, 0.02, 0.02));
  out.push(part(sphSm, "#111", hr * 0.55, hy + hr * 0.2, hz + hr * 0.45, 0, 0, 0, 0.02, 0.02, 0.02));
  out.push(part(box, dark, -hr * 1.05, hy + hr * 0.15, hz, 0, 0, 0.7, hr * 0.7, 0.025, hr * 0.35));
  out.push(part(box, dark, hr * 1.05, hy + hr * 0.15, hz, 0, 0, -0.7, hr * 0.7, 0.025, hr * 0.35));
  if (cow) {
    out.push(part(cone, "#f3ecdc", -hr * 0.55, hy + hr * 0.85, hz - 0.02, 0.2, 0, 0.8, 0.03, 0.2, 0.03));
    out.push(part(cone, "#f3ecdc", hr * 0.55, hy + hr * 0.85, hz - 0.02, 0.2, 0, -0.8, 0.03, 0.2, 0.03));
  }
  if (goat) {
    out.push(part(cone, "#f4efe4", -0.04, hy + hr, hz - 0.02, 0.15, 0, 0.45, 0.02, 0.18, 0.02));
    out.push(part(cone, "#f4efe4", 0.04, hy + hr, hz - 0.02, 0.15, 0, -0.45, 0.02, 0.18, 0.02));
    out.push(part(cone, coat, 0, hy - hr * 0.85, hz + 0.04, Math.PI, 0, 0, 0.035, 0.1, 0.035));
  }
  if (deer) {
    for (const s of [-1, 1]) {
      out.push(part(cyl, "#e6d4b4", s * 0.045, hy + 0.16, hz - 0.02, 0, 0, s * 0.3, 0.012, 0.2, 0.012));
      out.push(part(cyl, "#e6d4b4", s * 0.1, hy + 0.26, hz - 0.02, 0, 0, s * 1.0, 0.008, 0.12, 0.008));
      out.push(part(cyl, "#e6d4b4", s * 0.02, hy + 0.22, hz + 0.02, 0, 0, s * -0.6, 0.008, 0.1, 0.008));
    }
  }
  if (lion) {
    out.push(part(sph, "#8a5420", 0, hy, hz - 0.02, 0, 0, 0, hr * 1.85, hr * 1.7, hr * 1.45));
  }
  out.push(part(cone, dark, 0, shoulder, -len * 0.55, 1.2, 0, 0, 0.025, deer ? 0.16 : 0.22, 0.025));
  out.push(part(sph, sheep ? "#111" : coat, 0, shoulder - 0.04, -len * 0.62, 0, 0, 0, 0.045, 0.045, 0.045));
  const lx = wide * 0.55;
  const fz = len * 0.32;
  leg(out, -lx, fz, dark, hoof, 0.05, wide * 0.22, shoulder);
  leg(out, lx, fz * 0.9, dark, hoof, -0.02, wide * 0.22, shoulder);
  leg(out, -lx, -fz, dark, hoof, -0.02, wide * 0.24, shoulder);
  leg(out, lx, -fz * 0.92, dark, hoof, 0.04, wide * 0.24, shoulder);
  return out;
}

const beastCache = new Map<string, THREE.BufferGeometry>();

export function BeastMesh({ kind, gait }: { kind: string; gait?: React.MutableRefObject<Gait> }) {
  const geo = useMemo(() => {
    let g = beastCache.get(kind);
    if (!g) {
      g = beastGeo(kind);
      beastCache.set(kind, g);
    }
    return g;
  }, [kind]);
  return (
    <mesh geometry={geo} castShadow receiveShadow scale={1.35}>
      <WalkMat gait={gait} roughness={0.78} metalness={0.02} />
    </mesh>
  );
}

function personGeo(role: string, accent: string) {
  const skin = "#e7b48a";
  const hair = role === "worker" ? "#8a4a28" : "#4a2c18";
  const cloth =
    role === "healer" ? "#f7f3ea" : role === "cannon" ? "#3a342e" : role === "militia" ? "#6b4a30" : accent;
  const linen = "#f2e6d0";
  const steel = "#b7c0c8";
  const out: THREE.BufferGeometry[] = [];
  out.push(part(box, "#c4a06a", -0.09, 0.035, 0.03, 0, 0, 0, 0.11, 0.05, 0.18, 1, 0.62));
  out.push(part(box, "#c4a06a", 0.09, 0.035, 0.03, 0, 0, 0, 0.11, 0.05, 0.18, 2, 0.62));
  out.push(part(cap, skin, -0.08, 0.34, 0, 0, 0, 0, 0.06, 0.26, 0.06, 1, 0.62));
  out.push(part(cap, skin, 0.08, 0.34, 0, 0, 0, 0, 0.06, 0.26, 0.06, 2, 0.62));
  const body =
    role === "man" ? steel : role === "spear" || role === "crossbow" ? "#c4b49a" : cloth;
  out.push(part(box, body, 0, 0.68, 0, 0, 0, 0, 0.34, 0.32, 0.2));
  out.push(part(box, body, 0, 0.98, 0, 0, 0, 0, 0.3, 0.28, 0.18));
  if (role === "worker") {
    out.push(part(box, linen, 0, 1.02, 0.08, 0, 0, 0, 0.16, 0.18, 0.04));
  }
  if (role === "man") {
    out.push(part(box, steel, 0, 1.05, 0.08, 0, 0, 0, 0.22, 0.16, 0.06));
  }
  out.push(part(box, accent, 0, 0.84, 0.08, 0, 0, 0, 0.36, 0.07, 0.08));
  out.push(part(cap, skin, -0.24, 0.9, 0.05, 0.2, 0, 0.45, 0.05, 0.22, 0.05, 5, 1.08));
  out.push(part(cap, skin, 0.24, 0.88, 0.08, 0.25, 0, -0.55, 0.05, 0.24, 0.05, 6, 1.08));
  out.push(part(box, body, -0.2, 1.05, 0, 0, 0, 0.35, 0.09, 0.1, 0.09, 5, 1.08));
  out.push(part(box, body, 0.2, 1.05, 0.02, 0, 0, -0.35, 0.09, 0.1, 0.09, 6, 1.08));
  out.push(part(cyl, skin, 0, 1.18, 0.01, 0, 0, 0, 0.055, 0.08, 0.055));
  out.push(part(sph, skin, 0, 1.36, 0.03, 0, 0, 0, 0.14, 0.16, 0.14));
  out.push(part(sph, hair, 0, 1.46, -0.02, 0, 0, 0, 0.15, 0.1, 0.15));
  out.push(part(box, hair, 0, 1.34, -0.1, 0, 0, 0, 0.15, 0.12, 0.07));
  out.push(part(box, hair, -0.12, 1.32, 0.02, 0, 0, 0, 0.04, 0.1, 0.1));
  out.push(part(box, hair, 0.12, 1.32, 0.02, 0, 0, 0, 0.04, 0.1, 0.1));
  out.push(part(sphSm, "#1a120c", -0.05, 1.38, 0.15, 0, 0, 0, 0.02, 0.022, 0.012));
  out.push(part(sphSm, "#1a120c", 0.05, 1.38, 0.15, 0, 0, 0, 0.02, 0.022, 0.012));
  out.push(part(box, "#c48a78", 0, 1.3, 0.15, 0, 0, 0, 0.03, 0.012, 0.02));
  if (role === "healer") {
    out.push(part(box, "#f7f3ea", 0, 0.55, 0, 0, 0, 0, 0.42, 0.28, 0.24));
    out.push(part(box, "#e8d48a", 0, 1.05, 0.14, 0, 0, 0, 0.04, 0.2, 0.03));
    out.push(part(box, "#e8d48a", 0, 1.12, 0.14, 0, 0, 0, 0.14, 0.04, 0.03));
  } else if (role === "explorer") {
    out.push(part(box, "#d7c08a", 0, 1.48, 0, 0, 0, 0, 0.22, 0.06, 0.2));
    out.push(part(cyl, "#efe6d2", 0.3, 0.78, 0.12, 0.5, 0, -0.4, 0.035, 0.22, 0.035, 6, 1.08));
    out.push(part(box, "#6a5030", 0.34, 0.95, 0.16, 0, 0, 0, 0.08, 0.1, 0.02, 6, 1.08));
  } else if (role === "worker") {
    out.push(part(cyl, "#6a4a28", 0.32, 0.72, 0.16, 0.6, 0, -0.35, 0.025, 0.55, 0.025, 6, 1.08));
    out.push(part(box, "#b7b7b0", 0.46, 0.48, 0.22, 0.3, 0.3, 0, 0.12, 0.05, 0.035, 6, 1.08));
  } else if (role === "militia") {
    out.push(part(box, "#4a3424", 0, 1.48, 0, 0, 0, 0, 0.18, 0.06, 0.18));
    out.push(part(cyl, "#6a4a28", 0.28, 0.7, 0.12, 0.4, 0, -0.4, 0.03, 0.42, 0.03, 6, 1.08));
    out.push(part(sph, "#5a4030", 0.38, 0.5, 0.16, 0, 0, 0, 0.06, 0.06, 0.06, 6, 1.08));
  } else if (role === "archer") {
    out.push(part(box, "#5a4030", 0, 1.46, -0.04, 0, 0, 0, 0.2, 0.08, 0.16));
    out.push(part(cyl, "#c8a060", -0.22, 0.85, 0.1, 0.4, 0, 0.8, 0.012, 0.55, 0.012, 5, 1.08));
    out.push(part(cyl, "#8a6848", -0.22, 0.85, 0.18, 0, 0, 0.5, 0.01, 0.22, 0.01, 5, 1.08));
    out.push(part(box, "#6a5030", 0.16, 0.85, -0.1, 0, 0, 0, 0.06, 0.16, 0.08));
  } else if (role === "skirmisher") {
    out.push(part(box, "#6a5840", 0, 1.46, 0, 0, 0, 0, 0.16, 0.05, 0.16));
    out.push(part(cyl, "#c8a060", 0.2, 0.9, 0.1, 0.3, 0, -0.4, 0.012, 0.7, 0.012, 6, 1.08));
    out.push(part(cone, "#d0d6dc", 0.28, 1.2, 0.16, 0.3, 0, -0.4, 0.03, 0.1, 0.03, 6, 1.08));
  } else if (role === "crossbow") {
    out.push(part(cyl, steel, 0, 1.48, 0, 0, 0, 0, 0.13, 0.08, 0.13));
    out.push(part(box, "#5a4030", 0.22, 0.72, 0.16, 0, 0, 0, 0.32, 0.05, 0.08, 6, 1.08));
    out.push(part(cyl, "#c8a060", 0.22, 0.72, 0.16, 0, 0, Math.PI / 2, 0.012, 0.36, 0.012, 6, 1.08));
  } else if (role === "spear") {
    out.push(part(cyl, steel, 0, 1.5, 0, 0, 0, 0, 0.13, 0.1, 0.13));
    out.push(part(cyl, "#7a6848", 0.18, 0.95, 0.1, 0.05, 0, -0.08, 0.016, 1.15, 0.016, 6, 1.08));
    out.push(part(cone, "#d0d6dc", 0.2, 1.5, 0.14, 0.05, 0, -0.08, 0.035, 0.14, 0.035, 6, 1.08));
    out.push(part(box, accent, -0.24, 0.85, 0.1, 0, 0.2, 0, 0.06, 0.28, 0.18, 5, 1.08));
  } else if (role === "cannon") {
    out.push(part(box, "#2a2622", 0, 1.42, -0.02, 0, 0, 0, 0.22, 0.1, 0.2));
    out.push(part(cyl, "#2a2e32", 0.26, 0.7, 0.16, Math.PI / 2, 0, 0.2, 0.045, 0.36, 0.045, 6, 1.08));
    out.push(part(sph, "#3a4048", 0.08, 0.7, 0.1, 0, 0, 0, 0.05, 0.05, 0.05, 6, 1.08));
  } else if (role === "man") {
    out.push(part(cyl, steel, 0, 1.5, 0, 0, 0, 0, 0.14, 0.1, 0.14));
    out.push(part(box, steel, 0, 1.48, 0.12, 0, 0, 0, 0.12, 0.05, 0.04));
    out.push(part(box, "#d8dee4", 0.3, 0.85, 0.12, 0.15, 0, -0.5, 0.05, 0.28, 0.02, 6, 1.08));
    out.push(part(box, accent, -0.24, 0.82, 0.12, 0, 0.15, 0, 0.05, 0.26, 0.16, 5, 1.08));
  } else {
    out.push(part(cyl, "#b8c0c8", 0.22, 0.62, 0.06, 0.1, 0, -0.45, 0.02, 0.55, 0.025, 6, 1.08));
  }
  return fuse(out);
}

const personCache = new Map<string, { body: THREE.BufferGeometry; metal: THREE.BufferGeometry }>();

export function Person({
  role,
  accent,
  bob,
  gait,
}: {
  role: string;
  accent: string;
  bob: number;
  gait?: React.MutableRefObject<Gait>;
}) {
  const geo = useMemo(() => {
    const key = `${role}|${accent}`;
    let g = personCache.get(key);
    if (!g) {
      const body = personGeo(role, accent);
      g = { body, metal: body };
      personCache.set(key, g);
    }
    return g.body;
  }, [role, accent]);
  return (
    <group position={[0, bob, 0]} scale={1.35}>
      <mesh geometry={geo} castShadow receiveShadow>
        <WalkMat gait={gait} roughness={0.62} metalness={0.12} />
      </mesh>
    </group>
  );
}

export function CamelRider({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  const geo = useMemo(() => {
    const key = `camel|${accent}`;
    let g = beastCache.get(key);
    if (g) return g;
    const hide = "#c4a06a";
    const out: THREE.BufferGeometry[] = [];
    out.push(part(cap, hide, 0, 0.7, 0, Math.PI / 2, 0, 0, 0.22, 0.7, 0.24));
    out.push(part(sph, "#b89058", 0, 0.98, -0.05, 0, 0, 0, 0.2, 0.18, 0.22));
    const camelNeck = neckJoin(0.95, 0.28, 0.5, 0.11, 0.95);
    out.push(part(cap, hide, 0, camelNeck.cy, camelNeck.cz, camelNeck.ang, 0, 0, 0.08, 0.5, 0.09));
    out.push(part(sph, hide, 0, camelNeck.hy, camelNeck.hz, 0, 0, 0, 0.1, 0.1, 0.12));
    out.push(part(box, "#a08050", 0, camelNeck.hy - 0.04, camelNeck.hz + 0.12, 0, 0, 0, 0.08, 0.05, 0.1));
    out.push(part(cone, hide, -0.06, camelNeck.hy + 0.1, camelNeck.hz - 0.02, 0, 0, 0.3, 0.025, 0.08, 0.025));
    out.push(part(cone, hide, 0.06, camelNeck.hy + 0.1, camelNeck.hz - 0.02, 0, 0, -0.3, 0.025, 0.08, 0.025));
    leg(out, -0.12, 0.22, "#a08050", "#3a2818", 0.04, 0.07, 0.7);
    leg(out, 0.12, 0.2, "#a08050", "#3a2818", -0.02, 0.07, 0.7);
    leg(out, -0.12, -0.24, "#a08050", "#3a2818", -0.02, 0.07, 0.7);
    leg(out, 0.12, -0.22, "#a08050", "#3a2818", 0.04, 0.07, 0.7);
    out.push(part(box, "#6a4a30", 0, 1.05, -0.02, 0, 0, 0, 0.22, 0.06, 0.28));
    out.push(part(box, "#e6d3a8", 0, 1.22, 0.02, 0, 0, 0, 0.28, 0.36, 0.18));
    out.push(part(box, accent, 0, 1.18, 0.1, 0, 0, 0, 0.3, 0.06, 0.06));
    out.push(part(sph, "#d7b394", 0, 1.52, 0.04, 0, 0, 0, 0.09, 0.1, 0.09));
    out.push(part(box, "#6a4328", 0, 1.62, -0.02, 0, 0, 0, 0.12, 0.06, 0.12));
    out.push(part(cyl, "#c8ccd0", 0.22, 1.35, 0.12, 0.2, 0, -0.2, 0.016, 0.7, 0.016, 6, 1.2));
    g = fuse(out);
    beastCache.set(key, g);
    return g;
  }, [accent]);
  return (
    <group position={[0, bob * 0.35, 0]}>
      <mesh geometry={geo} castShadow receiveShadow>
        <WalkMat gait={gait} roughness={0.7} metalness={0.08} />
      </mesh>
    </group>
  );
}

export function WarElephant({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  const geo = useMemo(() => {
    const key = `elephant|${accent}`;
    let g = beastCache.get(key);
    if (g) return g;
    const hide = "#8d97a2";
    const dark = "#5e6872";
    const out: THREE.BufferGeometry[] = [];
    out.push(part(box, hide, 0, 0.72, 0, 0, 0, 0, 0.72, 0.62, 1.15));
    out.push(part(box, dark, 0, 0.95, 0.15, 0, 0, 0, 0.55, 0.18, 0.7));
    out.push(part(box, hide, 0, 0.78, 0.72, 0, 0, 0, 0.42, 0.4, 0.38));
    out.push(part(box, hide, -0.28, 0.82, 0.7, 0, 0.4, 0, 0.06, 0.28, 0.18));
    out.push(part(box, hide, 0.28, 0.82, 0.7, 0, -0.4, 0, 0.06, 0.28, 0.18));
    out.push(part(cyl, dark, 0, 0.42, 0.95, 0.9, 0, 0, 0.07, 0.55, 0.07));
    out.push(part(cone, "#f2e6c8", -0.1, 0.62, 0.92, 1.2, 0, 0.15, 0.035, 0.28, 0.035));
    out.push(part(cone, "#f2e6c8", 0.1, 0.62, 0.92, 1.2, 0, -0.15, 0.035, 0.28, 0.035));
    out.push(part(sphSm, "#1a120c", -0.1, 0.9, 0.9, 0, 0, 0, 0.03, 0.03, 0.02));
    out.push(part(sphSm, "#1a120c", 0.1, 0.9, 0.9, 0, 0, 0, 0.03, 0.03, 0.02));
    leg(out, -0.24, 0.32, hide, "#3a4048", 0.02, 0.11, 0.7);
    leg(out, 0.24, 0.32, hide, "#3a4048", -0.02, 0.11, 0.7);
    leg(out, -0.24, -0.32, hide, "#3a4048", -0.02, 0.11, 0.7);
    leg(out, 0.24, -0.32, hide, "#3a4048", 0.02, 0.11, 0.7);
    out.push(part(box, "#6a4030", 0, 1.15, -0.05, 0, 0, 0, 0.48, 0.08, 0.55));
    out.push(part(box, accent, 0, 1.38, -0.02, 0, 0, 0, 0.42, 0.32, 0.42));
    out.push(part(box, "#f4efe4", 0, 1.62, 0.02, 0, 0, 0, 0.28, 0.16, 0.28));
    out.push(part(cyl, "#c8ccd0", 0.22, 1.55, 0.12, 0.2, 0, -0.4, 0.02, 0.55, 0.02));
    g = fuse(out);
    beastCache.set(key, g);
    return g;
  }, [accent]);
  return (
    <group position={[0, bob * 0.25, 0]} scale={1.15}>
      <mesh geometry={geo} castShadow receiveShadow>
        <WalkMat gait={gait} roughness={0.72} metalness={0.04} />
      </mesh>
    </group>
  );
}
